import { useTranslation } from 'next-i18next'
import React from 'react'
import AnimatedSection from 'src/components/AnimatedSection'

/**
 * Trust signals — the page's one dark (ink-800) band, so the security
 * promises land as a distinct beat instead of another pale section. Each row
 * is a numbered italic accent + title + body, mirroring the rhythm of
 * `PersonalizedLanding/Comparison/index.tsx` and `Flotillas/HowItWorks.tsx`.
 *
 * Eyebrow + H2 + sub on the left rail (4 cols), rows on the right (8 cols).
 * No icons and no photo — the surrounding sections carry the imagery;
 * heaviness here comes from scale and the dark ground.
 *
 * Title/body keys are resolved via an explicit map (no dynamic template
 * literals — typed `t()` cannot validate computed keys).
 */

const TrustSignals = () => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const rows: { title: string; body: string }[] = [
    {
      title: t('TRUST.PARQUES_AAA'),
      body: t('TRUST.PARQUES_AAA_BODY')
    },
    {
      title: t('TRUST.VIGILANCIA'),
      body: t('TRUST.VIGILANCIA_BODY')
    },
    {
      title: t('TRUST.CCTV'),
      body: t('TRUST.CCTV_BODY')
    },
    {
      title: t('TRUST.ACCESO'),
      body: t('TRUST.ACCESO_BODY')
    },
    {
      title: t('TRUST.ASEGURADO'),
      body: t('TRUST.ASEGURADO_BODY')
    }
  ]

  return (
    <AnimatedSection
      className="w-full bg-ink-800 py-20 md:py-28"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <section
          aria-labelledby="trust-heading"
          className="md:grid md:grid-cols-12 md:gap-12"
        >
          {/* Left rail — eyebrow + H2 + subtitle. */}
          <div className="md:col-span-4">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('TRUST.EYEBROW')}
            </p>
            <h2
              id="trust-heading"
              className="mt-3 font-display text-[28px] leading-[1.12] font-medium tracking-[-0.01em] text-white-500 md:text-[40px]"
            >
              {t('TRUST.TITLE')}
            </h2>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-white-500/75 md:text-base">
              {t('TRUST.SUBTITLE')}
            </p>
          </div>

          {/* Hairline rows */}
          <ul className="mt-8 border-t border-white-500/15 md:col-span-8 md:mt-0">
            {rows.map(({ title, body }, index) => (
              <li
                key={title}
                className="flex items-start gap-5 border-b border-white-500/15 py-6 md:gap-8 md:py-7"
              >
                <span
                  aria-hidden="true"
                  className="font-display text-[28px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic md:text-[36px]"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <h3 className="font-display text-[18px] leading-tight font-medium text-white-500 md:text-[20px]">
                    {title}
                  </h3>
                  <p className="text-[15px] leading-relaxed text-white-500/70 md:text-base">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AnimatedSection>
  )
}

export default React.memo(TrustSignals)
